import { BrowserProvider } from 'ethers';
import { getAppChainConfig, isTestMode } from './network';

let web3auth = null;
let initialized = false;
let lastMode = null;

/** Client ID from build-time env or runtime (e.g. Vercel / window injection). */
function getWeb3AuthClientId() {
  const fromEnv = process.env.REACT_APP_WEB3AUTH_CLIENT_ID;
  if (fromEnv && fromEnv.trim()) return fromEnv.trim();
  if (typeof window !== 'undefined' && window.__REACT_APP_WEB3AUTH_CLIENT_ID) {
    return String(window.__REACT_APP_WEB3AUTH_CLIENT_ID).trim();
  }
  return '';
}

export function isWeb3AuthConfigured() {
  return !!getWeb3AuthClientId();
}

function getChainConfig() {
  const cfg = getAppChainConfig();
  const chainIdDec = cfg.chainId;
  const chainIdHex = `0x${chainIdDec.toString(16)}`;

  return {
    chainIdDec,
    chainConfig: {
      chainNamespace: 'eip155',
      chainId: chainIdHex,
      rpcTarget: cfg.rpcUrl,
      displayName: cfg.chainName,
      blockExplorerUrl: cfg.explorerUrl,
      ticker: cfg.nativeSymbol,
      tickerName: cfg.nativeName,
    },
  };
}

export async function initWeb3Auth() {
  // If the user flips ?test=true at runtime, re-init with the other chain config.
  const mode = isTestMode() ? 'test' : 'main';
  if (lastMode && lastMode !== mode) {
    initialized = false;
    web3auth = null;
  }
  lastMode = mode;

  if (initialized && web3auth) return web3auth;

  const clientId = getWeb3AuthClientId();
  if (!clientId) {
    throw new Error('Web3Auth is not configured. Missing REACT_APP_WEB3AUTH_CLIENT_ID.');
  }

  // Dynamic import keeps initial bundle smaller and avoids module-load issues until needed.
  // Support both named and default export (bundler/version differences can cause "default is not a function").
  const [modalMod, baseMod, ethProviderMod] = await Promise.all([
    import('@web3auth/modal'),
    import('@web3auth/base'),
    import('@web3auth/ethereum-provider'),
  ]);
  const Web3AuthCtor = modalMod.Web3Auth ?? modalMod.default;
  if (typeof Web3AuthCtor !== 'function') {
    throw new Error(
      'Web3Auth SDK: expected Web3Auth constructor. Check @web3auth/modal version and bundler.'
    );
  }
  const CHAIN_NAMESPACES = baseMod.CHAIN_NAMESPACES ?? { EIP155: 'eip155' };
  const EthereumPrivateKeyProvider = ethProviderMod.EthereumPrivateKeyProvider ?? ethProviderMod.default;

  const { chainConfig } = getChainConfig();
  const chainConfigForAuth = {
    chainNamespace: CHAIN_NAMESPACES.EIP155 ?? 'eip155',
    chainId: chainConfig.chainId,
    rpcTarget: chainConfig.rpcTarget,
    displayName: chainConfig.displayName,
    blockExplorerUrl: chainConfig.blockExplorerUrl,
    ticker: chainConfig.ticker,
    tickerName: chainConfig.tickerName,
  };

  if (typeof EthereumPrivateKeyProvider !== 'function') {
    throw new Error(
      'Web3Auth: EthereumPrivateKeyProvider not found. Check @web3auth/ethereum-provider is installed.'
    );
  }
  const privateKeyProvider = new EthereumPrivateKeyProvider({
    config: { chainConfig: chainConfigForAuth },
  });

  const network = (typeof window !== 'undefined' && window.__REACT_APP_WEB3AUTH_NETWORK) || process.env.REACT_APP_WEB3AUTH_NETWORK || 'sapphire_mainnet';
  const origin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
  if (origin && (process.env.NODE_ENV === 'development' || origin.includes('localhost'))) {
    console.info('[Web3Auth] Origin (add this EXACT value in dashboard Whitelist URL):', origin);
  }

  web3auth = new Web3AuthCtor({
    clientId,
    web3AuthNetwork: network,
    chainConfig: chainConfigForAuth,
    privateKeyProvider,
  });

  const doInit = async (useNetwork) => {
    const instance = useNetwork !== network
      ? new Web3AuthCtor({
          clientId,
          web3AuthNetwork: useNetwork,
          chainConfig: chainConfigForAuth,
          privateKeyProvider,
        })
      : web3auth;
    if (instance !== web3auth) web3auth = instance;
    if (typeof web3auth.initModal === 'function') {
      await web3auth.initModal();
    }
  };

  try {
    await doInit(network);
  } catch (err) {
    const msg = err?.message || String(err);
    const status = err?.response?.status ?? err?.status;
    const is400 =
      msg.toLowerCase().includes('failed to fetch project configurations') ||
      msg.toLowerCase().includes('wallet is not ready') ||
      msg.includes('400') ||
      msg.toLowerCase().includes('bad request') ||
      status === 400;
    if (is400 && network === 'sapphire_mainnet') {
      try {
        console.warn('[Web3Auth] Mainnet config returned 400. Retrying with sapphire_devnet (dev network).');
        web3auth = new Web3AuthCtor({
          clientId,
          web3AuthNetwork: 'sapphire_devnet',
          chainConfig: chainConfigForAuth,
          privateKeyProvider,
        });
        await web3auth.initModal();
        initialized = true;
        return web3auth;
      } catch (devnetErr) {
        console.warn('[Web3Auth] Devnet also failed:', devnetErr?.message || devnetErr);
      }
    }
    if (is400) {
      const hint = [
        'Web3Auth config failed (400). Add your site origin in the Web3Auth dashboard.',
        '',
        '1) Go to https://dashboard.web3auth.io → your Plug and Play project (same Client ID as in the app).',
        '2) Whitelist URL / Allowed Origins: add this EXACT URL (no trailing slash):',
        `   ${origin || 'https://www.ewio.org'}`,
        '3) If you use multiple domains, add each (e.g. https://ewio.org, https://io.vercel.app).',
        '4) Save, wait 1–2 min, then hard refresh (Ctrl+Shift+R) and try again.',
        '',
        'If mainnet (sapphire_mainnet) is not enabled for your project, the app will try devnet (sapphire_devnet) as fallback.',
      ].join('\n');
      if (process.env.NODE_ENV === 'development') {
        console.error('[Web3Auth] Full error:', err);
      }
      throw new Error(hint);
    }
    throw err;
  }
  initialized = true;
  return web3auth;
}

export async function connectWeb3Auth() {
  const w3a = await initWeb3Auth();
  const extProvider = await w3a.connect();
  if (!extProvider) {
    throw new Error('Web3Auth did not return a provider.');
  }
  const provider = new BrowserProvider(extProvider);
  const signer = await provider.getSigner();
  return { provider, signer };
}

export async function disconnectWeb3Auth() {
  if (!web3auth) return;
  try {
    await web3auth.logout();
  } finally {
    // Keep initialized=true so initModal isn't re-run unnecessarily.
  }
}

export function isWeb3AuthConnected() {
  return Boolean(web3auth && web3auth.connected && web3auth.provider);
}

export async function getWeb3AuthAddress() {
  const { signer } = await getWeb3AuthProvider();
  return await signer.getAddress();
}

export async function getWeb3AuthProvider() {
  const w3a = await initWeb3Auth();
  if (!w3a.provider) {
    throw new Error('Web3Auth not connected.');
  }
  const provider = new BrowserProvider(w3a.provider);
  const signer = await provider.getSigner();
  return { provider, signer };
}
